import {
  DestroyRef,
  EnvironmentProviders,
  ErrorHandler,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { CombinedGraphQLErrors } from '@apollo/client/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import type { LngLatLike, Map as MapboxMap } from 'mapbox-gl';
import { environment } from '@environments/environment';
import {
  isStaleBundleError,
  readAppBuildId,
} from '@services/app-version-guard.service';

type ReplayCanvas = ReturnType<typeof Sentry.replayCanvasIntegration>;

/**
 * Starts the Sentry SDK. Must run before `bootstrapApplication` so errors
 * thrown during bootstrap are captured too.
 *
 * Only CI builds report: they stamp the build id into index.html, which also
 * becomes the release, so local `ng serve` stays silent.
 */
export function initSentry(): void {
  const buildId = readAppBuildId();

  if (!environment.sentry.dsn || !buildId) {
    return;
  }

  // Real customer data only lives in production and polt-ai: replays there are
  // masked and skip canvases (maps, charts), which masking can't cover.
  // Staging, dev and feature replays stay unmasked and record canvases so
  // they are useful for debugging.
  const isRealProduction =
    environment.production &&
    ['production', 'polt-ai'].includes(environment.sentry.environment);

  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: `nexus-ui@${buildId}`,
    integrations: [
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest: shouldTraceRequest,
      }),
      // Record only sessions that hit an error.
      Sentry.replayIntegration({
        maskAllText: isRealProduction,
        maskAllInputs: isRealProduction,
        blockAllMedia: isRealProduction,
      }),
      // Manual snapshots: automatic canvas recording forces
      // `preserveDrawingBuffer` on every WebGL context, which slows Mapbox.
      ...(isRealProduction
        ? []
        : [
            Sentry.replayCanvasIntegration({
              enableManualSnapshot: true,
              quality: 'low',
            }),
          ]),
    ],
    tracesSampleRate: environment.production ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // The user is identified explicitly below (id + organization only); keep
    // the SDK from adding IP addresses or GraphQL request/response bodies.
    dataCollection: {
      userInfo: false,
      httpBodies: [],
    },
    // Playwright and other automated browsers set `navigator.webdriver`. Their
    // events stay (they surface real bugs) but can be filtered out by tag.
    initialScope: {
      tags: { automation: navigator.webdriver === true },
    },
    beforeBreadcrumb: trackIconSpriteRequest,
    beforeSend,
  });

  if (!isRealProduction) {
    startIdleCanvasSnapshots();
  }
}

/**
 * `{type}: {value}` of every exception in the event, falling back to its
 * message. `@sentry/angular` unwraps `HttpErrorResponse`s before capturing, so
 * this is often the only place the original HTTP failure text survives.
 */
function eventMessage(event: Sentry.ErrorEvent): string {
  const exceptions = (event.exception?.values ?? []).map(
    ({ type, value }) => `${type}: ${value}`
  );

  return exceptions.length ? exceptions.join('\n') : (event.message ?? '');
}

/** The browser's own messages for a fetch that got no response at all. */
const FETCH_FAILURE =
  /^TypeError: (Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)( \(.+\))?$/m;
/**
 * `HttpErrorResponse.message` for status 0, as rethrown or wrapped by others.
 * When Angular's fetch backend fails before it has a response (e.g. the page
 * is being left), it builds the error without url or status, which reads
 * "(unknown url): undefined undefined" although the status is 0 (NEXUS-UI-1Q).
 */
const HTTP_STATUS_0 =
  /Http failure response for (\(unknown url\)|\S+): (0|undefined undefined)\b/;

/** The `nx:` icon sprite that `mat-icon` loads every app icon from. */
const ICON_SPRITE_URL = 'assets/icons/symbol-defs.svg';

/**
 * When Material last reported that the icon sprite failed to load (event
 * timestamp, in seconds). The sprite's fetch breadcrumb doesn't always record
 * the failure, e.g. when the page is being left, yet every icon requested
 * afterwards fails with its own "Error retrieving icon" (NEXUS-UI-1R).
 */
let iconSpriteFailedAt: number | undefined;

/**
 * When a request for the icon sprite last succeeded (breadcrumb timestamp, in
 * seconds). Recorded as it happens: an event only carries the latest
 * breadcrumbs, so a recovery can have dropped out of them by the next error.
 */
let iconSpriteLoadedAt: number | undefined;

/** Forgets the recorded sprite failure and recovery. For tests. */
export function resetIconSpriteState(): void {
  iconSpriteFailedAt = undefined;
  iconSpriteLoadedAt = undefined;
}

/**
 * `beforeBreadcrumb` hook: records a successful icon sprite request. Returns
 * the breadcrumb unchanged.
 */
export function trackIconSpriteRequest(
  breadcrumb: Sentry.Breadcrumb
): Sentry.Breadcrumb {
  const status = Number(breadcrumb.data?.['status_code']);

  if (
    breadcrumb.category === 'fetch' &&
    String(breadcrumb.data?.['url']).includes(ICON_SPRITE_URL) &&
    status >= 200 &&
    status < 400
  ) {
    iconSpriteLoadedAt = breadcrumb.timestamp ?? Date.now() / 1000;
  }

  return breadcrumb;
}

/** Records the sprite failure if `message` is Material reporting one. */
function noteIconSpriteFailure(
  event: Sentry.ErrorEvent,
  message: string
): void {
  if (message.includes(`Loading icon set URL: ${ICON_SPRITE_URL}`)) {
    iconSpriteFailedAt = event.timestamp ?? Date.now() / 1000;
  }
}

/**
 * Whether the sprite failure on record is the latest news about the sprite:
 * no request for it has succeeded since.
 */
function iconSpriteStillFailed(): boolean {
  return (
    iconSpriteFailedAt !== undefined &&
    (iconSpriteLoadedAt === undefined ||
      iconSpriteLoadedAt <= iconSpriteFailedAt)
  );
}

/**
 * A request that never got a response: offline, blocked, or aborted because
 * the page was closed or left. Says nothing about the app.
 */
function isNetworkFailure(error: unknown, message: string): boolean {
  return (
    (error instanceof HttpErrorResponse && error.status === 0) ||
    HTTP_STATUS_0.test(message) ||
    FETCH_FAILURE.test(message)
  );
}

/** The fetch breadcrumb of the latest request to a URL containing `urlPart`. */
function latestRequest(
  event: Sentry.ErrorEvent,
  urlPart: string
): Sentry.Breadcrumb | undefined {
  return event.breadcrumbs
    ?.filter(
      ({ category, data }) =>
        category === 'fetch' && String(data?.['url']).includes(urlPart)
    )
    .at(-1);
}

/**
 * Whether the latest request to a URL containing `urlPart` failed, per its
 * fetch breadcrumb. Only the latest counts: once a retry has succeeded, an
 * error that depends on that request is a real one.
 */
function latestRequestFailed(
  event: Sentry.ErrorEvent,
  urlPart: string
): boolean {
  const request = latestRequest(event, urlPart);

  return (
    !!request &&
    (request.level === 'error' || request.data?.['status_code'] === 0)
  );
}

/**
 * `mat-icon` failing to find an `nx:` icon because the sprite holding it never
 * loaded (see {@link isNetworkFailure}), rather than because it is missing.
 */
function isIconFromFailedSprite(
  event: Sentry.ErrorEvent,
  message: string
): boolean {
  return (
    message.includes('Error retrieving icon nx:') &&
    (latestRequestFailed(event, ICON_SPRITE_URL) || iconSpriteStillFailed())
  );
}

/**
 * `angular-auth-oidc-client` failing to load the OIDC discovery document. It
 * rethrows the request's `HttpErrorResponse` as `new Error(response)`, so all
 * that reaches Sentry is "[object Object]" (NEXUS-UI-16); the request's
 * breadcrumb is the only trace of the network failure behind it.
 */
function isOidcConfigFromFailedRequest(
  event: Sentry.ErrorEvent,
  message: string
): boolean {
  return (
    message === 'Error: [object Object]' &&
    latestRequestFailed(event, '/.well-known/openid-configuration')
  );
}

/**
 * Apollo's code for "The link chain completed without emitting a value".
 * Production builds report `go.apollo.dev/c/err#{version, message: code}`
 * instead of the text (NEXUS-UI-25). Codes are renumbered per Apollo release:
 * look the message up in `@apollo/client/invariantErrorCodes.js` on upgrade.
 */
export const APOLLO_LINK_CHAIN_EMPTY = { version: '4.3.1', message: 104 };

function isApolloLinkChainEmpty(message: string): boolean {
  if (message.includes('The link chain completed without emitting a value')) {
    return true;
  }

  const encoded = /go\.apollo\.dev\/c\/err#(\S+)/.exec(message)?.[1];
  if (!encoded) {
    return false;
  }

  try {
    const details = JSON.parse(decodeURIComponent(encoded));

    return (
      details?.version === APOLLO_LINK_CHAIN_EMPTY.version &&
      details?.message === APOLLO_LINK_CHAIN_EMPTY.message
    );
  } catch {
    return false;
  }
}

/**
 * Apollo's invariant for a link chain that completed without a result, after
 * the latest GraphQL request got a 401. That is the library's `AuthInterceptor`
 * failing to refresh the token: it logs the user out and completes the request
 * with `EMPTY`, which Apollo Client 4 rejects (NEXUS-UI-1J).
 */
function isRequestCancelledByLogout(
  event: Sentry.ErrorEvent,
  message: string
): boolean {
  return (
    isApolloLinkChainEmpty(message) &&
    latestRequest(event, '/api/graphql')?.data?.['status_code'] === 401
  );
}

/**
 * The exception that was thrown, without the errors it links as its `cause`:
 * Sentry lists the causes first and the thrown error last. An app error
 * wrapping a library error is the app's own, so filters for library errors
 * look only at this one.
 */
function thrownException(
  event: Sentry.ErrorEvent
): Sentry.Exception | undefined {
  return event.exception?.values?.at(-1);
}

/**
 * Mapbox's own errors that say nothing about the app:
 * - its telemetry callback running after `map.remove()`, which nulls its
 *   `errorCb`: a failed `events.mapbox.com` request for a map already
 *   destroyed then throws (NEXUS-UI-2T);
 * - a frame drawn on a WebGL context that was just lost (an iPad under memory
 *   pressure, a background tab): `gl.createBuffer()` returns null and Mapbox
 *   throws "Failed to create header UBO buffer" (NEXUS-UI-31). The map redraws
 *   once the context is restored.
 */
const MAPBOX_NOISE =
  /this\.errorCb is not a function|Failed to create (header|properties) UBO buffer/;

/** The script mapbox-gl runs from, once {@link locateMapbox} has found it. */
let mapboxScriptUrl: string | undefined;

/**
 * Finds the script mapbox-gl runs from. `beforeSend` sees frames before source
 * maps apply, and a production build ships Mapbox inside a hashed chunk, so
 * its frames never name `mapbox-gl`. Mapbox is made to throw a harmless error
 * and the URL is read from that error's innermost frame. Loads mapbox-gl if
 * nothing has yet; resolves to `undefined` if it can't be located.
 */
export async function locateMapbox(): Promise<string | undefined> {
  if (!mapboxScriptUrl) {
    try {
      const { LngLat } = await import('mapbox-gl');
      LngLat.convert(undefined as unknown as LngLatLike);
    } catch (error) {
      mapboxScriptUrl = Sentry.defaultStackParser(
        error instanceof Error ? (error.stack ?? '') : ''
      ).at(-1)?.filename;
    }
  }

  return mapboxScriptUrl;
}

/** Whether the event is one of {@link MAPBOX_NOISE}, thrown inside Mapbox. */
function isMapboxNoise(event: Sentry.ErrorEvent): boolean {
  const thrown = thrownException(event);

  return (
    MAPBOX_NOISE.test(thrown?.value ?? '') &&
    !!mapboxScriptUrl &&
    thrown?.stacktrace?.frames?.at(-1)?.filename === mapboxScriptUrl
  );
}

/**
 * `beforeSend`: drops noise, fingerprints GraphQL errors. An event that may be
 * Mapbox's noise first waits for {@link locateMapbox}, which needs a moment
 * the first time.
 */
async function beforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Promise<Sentry.ErrorEvent | null> {
  if (MAPBOX_NOISE.test(thrownException(event)?.value ?? '')) {
    await locateMapbox();
  }

  return isNoiseEvent(event, hint.originalException)
    ? null
    : withGraphQLFingerprint(event, hint.originalException);
}

/**
 * An error thrown inside Hotjar's own scripts, e.g. its `window.opener`
 * message on navigation after the opener has gone (NEXUS-UI-34). Nothing the
 * app can fix, and the app itself is unaffected. Hotjar loads from its own
 * host, so unlike the app's chunks its frames keep a recognisable URL.
 */
function isHotjarError(event: Sentry.ErrorEvent): boolean {
  const thrownFrom =
    thrownException(event)?.stacktrace?.frames?.at(-1)?.filename;

  return /^https?:\/\/[\w.-]*\.hotjar\.com\//.test(thrownFrom ?? '');
}

/**
 * Events dropped before sending: network failures (and the icons a failed
 * sprite then cannot find, or the OIDC config that then fails to load), GraphQL
 * requests cancelled by a logout after an expired session, Mapbox's own noise
 * (once {@link locateMapbox} has found its script), errors inside Hotjar's
 * scripts, and lazy chunks removed by a deploy, which
 * `AppVersionGuardService` recovers from by reloading.
 */
export function isNoiseEvent(
  event: Sentry.ErrorEvent,
  error: unknown
): boolean {
  const message = eventMessage(event);

  noteIconSpriteFailure(event, message);

  return (
    isStaleBundleError(error) ||
    isStaleBundleError(message) ||
    isNetworkFailure(error, message) ||
    isIconFromFailedSprite(event, message) ||
    isOidcConfigFromFailedRequest(event, message) ||
    isRequestCancelledByLogout(event, message) ||
    isMapboxNoise(event) ||
    isHotjarError(event)
  );
}

/** Masks UUIDs and numbers, which vary between occurrences of one error. */
function maskIds(message: string): string {
  return message
    .replace(/[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi, '<id>')
    .replace(/\d+/g, '<n>');
}

/**
 * Groups GraphQL errors by what failed instead of by stack trace. Every
 * unhandled GraphQL error is thrown from the same place in Apollo, so Sentry's
 * default grouping put unrelated backend errors into one issue (NEXUS-UI-E).
 * The fingerprint is each error's root field and message, with ids masked.
 */
export function withGraphQLFingerprint(
  event: Sentry.ErrorEvent,
  error: unknown
): Sentry.ErrorEvent {
  if (!CombinedGraphQLErrors.is(error) || !error.errors.length) {
    return event;
  }

  const errorKeys = error.errors.map(
    ({ path, message }) => `${path?.[0] ?? ''}: ${maskIds(message)}`
  );

  return {
    ...event,
    fingerprint: ['graphql', ...[...new Set(errorKeys)].sort()],
  };
}

/** Matches the canvas integration's `low` quality, which records 1 frame a second. */
const CANVAS_SNAPSHOT_INTERVAL_MS = 1000;
/** A little over the interval, so timer jitter never lands inside the SDK's throttle. */
const IDLE_SNAPSHOT_TICK_MS = 1100;

const recordedMaps = new Set<MapboxMap>();
let lastCanvasSnapshotAt = 0;

function replayCanvas(): ReplayCanvas | undefined {
  return Sentry.getClient()?.getIntegrationByName<ReplayCanvas>('ReplayCanvas');
}

/**
 * Snapshots every canvas on the page into the replay. Must run in the same
 * task that drew any WebGL canvas that should come out non-blank.
 */
function snapshotCanvases(): void {
  lastCanvasSnapshotAt = performance.now();
  void replayCanvas()?.snapshot(undefined, { skipRequestAnimationFrame: true });
}

/**
 * Keeps 2D canvases (charts, PDF pages) recorded while no map is drawing. With
 * a recorded map on the page it asks the map to repaint instead, so the map's
 * render handler takes the snapshot while its WebGL buffer is still readable.
 */
function startIdleCanvasSnapshots(): void {
  setInterval(() => {
    if (
      document.hidden ||
      performance.now() - lastCanvasSnapshotAt < CANVAS_SNAPSHOT_INTERVAL_MS
    ) {
      return;
    }

    if (recordedMaps.size) {
      recordedMaps.forEach((map) => map.triggerRepaint());
    } else {
      snapshotCanvases();
    }
  }, IDLE_SNAPSHOT_TICK_MS);
}

/**
 * Records a Mapbox map in non-production session replays. Mapbox draws WebGL
 * without `preserveDrawingBuffer`, so its canvas can only be read from the
 * `render` event of the frame that drew it. Stops when `destroyRef` fires.
 */
export function recordMapInReplay(
  map: MapboxMap,
  destroyRef: DestroyRef
): void {
  if (!replayCanvas() || destroyRef.destroyed) {
    return;
  }

  const onRender = (): void => {
    if (
      performance.now() - lastCanvasSnapshotAt >=
      CANVAS_SNAPSHOT_INTERVAL_MS
    ) {
      snapshotCanvases();
    }
  };

  recordedMaps.add(map);
  map.on('render', onRender);
  destroyRef.onDestroy(() => {
    recordedMaps.delete(map);
    map.off('render', onRender);
  });
}

/**
 * Skips `*.mapbox.com` (tiles, styles, sprites, glyphs, telemetry): Mapbox fetches
 * them in parallel bursts, which Sentry reports as N+1 API calls, and its URLs
 * carry the access token. Also skips URLs that can't be parsed, which Sentry would
 * name its span after verbatim, query string included.
 */
function shouldTraceRequest(url: string): boolean {
  try {
    return !/(^|\.)mapbox\.com$/.test(new URL(url, location.href).hostname);
  } catch {
    return false;
  }
}

/** Sentry error handler, router tracing and user context. */
export function provideSentry(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);

      inject(OidcSecurityService)
        .userData$.pipe(takeUntilDestroyed())
        .subscribe(({ userData }) => {
          Sentry.setUser(userData?.sub ? { id: userData.sub } : null);
          // The `organizations` claim is keyed by the user's organization.
          Sentry.setTag(
            'organization',
            Object.keys(userData?.organizations ?? {})[0]
          );
        });
    }),
  ]);
}
